import React, { useState, useEffect } from 'react';
import styles from '../../../Onboarding/Selector/Selector.styles';
import AutomaticBookingTest from 'Components/common/ConfigurationForm/AutomaticBookingTest/AutomaticBookingTest';
import TestResult from 'Components/common/ConfigurationForm/AutomaticConfigurationTest/TestResult/TestResult';
import useGuestHook from 'Components/Dashboard/PMS/PMSSelector/Guest/hooks/Guest.hook';
import useFormsHook from 'Components/Dashboard/Forms/Forms.hook.js';
import { useSupportedFields } from 'Components/Dashboard/CheckIn/GuestTable/useSupportedFields';
import usePMSUDFConfigHook from '../../PMSUDFConfig/PMSUDFConfig.hook';

export default function PmsDataRecovery({ hotelId, state, actions, translate, selectedPms, API }) {
  let guestDetails = actions.mergeCustomFieldsAndCamelCase(
    state.searchByCodeResponse?.guestDetails ||
      state.searchByCodeResponse?.startCheckin?.guestDetails
  );
  let checkinGuests = state.searchByCodeResponse?.startCheckin?.checkinGuests;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedGuest = guestDetails?.[selectedIndex];
  const [supportedFields, setSupportedFields] = useState(null);
  const [requiresDocuments, setRequiresDocuments] = useState(null);
  const { snakeToCamel } = useGuestHook();
  const { state: formsState } = useFormsHook();
  const { pmsModuleName } = useSupportedFields();
  const { state: pmsUdfState } = usePMSUDFConfigHook();

  let fields;
  if (selectedGuest) {
    if (formsState.isDynamicForm) {
      const formsFieldsActive = formsState.form?.holder_1?.filter(field => field.state == 'active');
      formsFieldsActive.forEach(field => {
        if (field.name === 'nationality') field.name = 'document_country';
        if (field.name === 'guest_nationality') field.name = 'nationality';
      });
      const camelCaseFormsFieldsActive = formsFieldsActive?.map(form => snakeToCamel(form.name));
      fields = Object.keys(selectedGuest)?.filter(field => {
        if (field == 'pmsIdentifier') return true;
        return camelCaseFormsFieldsActive?.includes(snakeToCamel(field));
      });
    } else {
      const formsFieldsActive = formsState.form?.filter(field => field.state == 'active');
      const camelCaseFormsFieldsActive = formsFieldsActive?.map(form => snakeToCamel(form.name));
      fields = Object.keys(selectedGuest)?.filter(field => {
        if (field == 'pmsIdentifier') return true;
        return camelCaseFormsFieldsActive?.includes(snakeToCamel(field));
      });
    }
    fields.sort((a, b) => {
      if (a === 'pmsIdentifier') return -1;
      if (b === 'pmsIdentifier') return 1;
      return 0;
    });
  }

  useEffect(() => {
    const fetchPmsConfig = async () => {
      try {
        const config = await API.pmsConfigDynamicFormRequest({ pmsId: selectedPms.id });

        let supportedFields = config.config_dynamic_form?.supportedFields?.map(
          field => field.civitfun
        );
        setSupportedFields(supportedFields);
      } catch (error) {
        console.error('Error loading PMS config:', error);
      }
    };

    const fetchConfigCheckin = async () => {
      API.checkinConfigRequest(hotelId).then(res => {
        setRequiresDocuments(res?.required_documents);
      });
    };

    fetchPmsConfig();
    fetchConfigCheckin();
  }, [selectedPms.id]);

  return (
    <div style={styles.flexContainer}>
      {selectedPms ? (
        <>
          {/* First div with fixed width */}
          <div style={styles.leftPanel}>
            <AutomaticBookingTest
              hotelId={hotelId}
              serviceName={selectedPms.name}
              buttonText={translate('pmsScreen.testPaperless')}
              buttonAction={actions.doSearchByCodeTest}
              isTesting={state.isTestingBooking}
              testResult={state.testBookingResult}
              description={translate('pmsScreen.testPaperlessDesc')}
              inputs={Object.values(selectedPms.configBookingSearchForm || {})[0]}
            />
          </div>

          {/* Second div that contains the rest */}
          <div style={styles.rightPanel}>
            {/* Show results directly without loading indicator */}
            {state.showResults && (
              <div style={styles.testMessageContainer}>
                <p style={styles.title}>{translate('selector.tabs.result')}</p>

                {/**Manage errors */}
                {state.searchByCodeResult !== null &&
                  (state.searchByCodeResult?.success === false ||
                    state.searchByCodeResult === false) && (
                    <div>
                      <TestResult
                        success={false}
                        testName={translate('searchesResults.searchByCodeResult')}
                        errorCode={state.searchByCodeResponse?.errorCode}
                      />
                    </div>
                  )}
                {state.searchByCodeResult && state.searchByCodeResponse?.status === -2 && (
                  <div>
                    <TestResult
                      success={false}
                      testName={translate('pmsScreen.testPaperlessMultiReservation')}
                    />
                    <div style={{ marginTop: '16px' }}>
                      <ul>
                        {state.searchByCodeResponse?.bookings?.map((booking, i) => (
                          <li key={booking.bookingIdentifier}>
                            <span style={{ whiteSpace: 'nowrap' }}>
                              <strong>
                                {translate('pmsScreen.testPaperlessRoom')} {i + 1}:
                              </strong>
                              &nbsp;
                              {booking.bookingIdentifier}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/**Manage response */}
                {state.searchByCodeResult && state.searchByCodeResponse && (
                  <div>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                      {guestDetails &&
                        filterByRequiresDocumentsConfig(
                          guestDetails,
                          requiresDocuments,
                          state.searchByCodeResponse?.bookings
                        )?.map((guest, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedIndex(index)}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              backgroundColor: index === selectedIndex ? '#007bff' : '#eee',
                              color: index === selectedIndex ? '#fff' : '#000',
                              border: 'none',
                              borderRadius: '4px',
                            }}
                          >
                            <>
                              {checkinGuests?.[index] ? (
                                <>
                                  {checkinGuests[index].holder
                                    ? `${translate('pmsScreen.holderLabel')}:`
                                    : (checkinGuests[index].type === 'adult'
                                        ? translate('pmsScreen.adultLabel')
                                        : translate('pmsScreen.childLabel') ||
                                          translate('pmsScreen.guestLabel')) + ':'}{' '}
                                </>
                              ) : (
                                `${translate('pmsScreen.guestLabel')}: `
                              )}
                              {guest.name || translate('pmsScreen.guestNameNotAvaliable')}
                            </>
                          </button>
                        ))}
                    </div>

                    <div>
                      {fields?.length
                        ? actions
                            .excludeNonPmsVariables(fields, supportedFields, selectedGuest)
                            ?.map((key, i) =>
                              typeof selectedGuest[key] === 'string' ? (
                                <p key={i}>
                                  <strong>{actions.parseToLabel(key)}: </strong>{' '}
                                  {selectedGuest[key] ||
                                    actions.isNonPmsVariable({
                                      key,
                                      supportedFields,
                                      pmsModuleName,
                                      pmsUdfState,
                                      snakeToCamel,
                                    })}
                                </p>
                              ) : null
                            )
                        : state?.searchByCodeResponse?.status !== -2 &&
                          translate('pmsScreen.guestInformationNoAvaliable')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div>{translate('pmsScreen.noPmsSelected')}</div>
      )}
    </div>
  );
}

function filterByRequiresDocumentsConfig(guestDetails, requiresDocuments, bookigns) {
  if (!requiresDocuments || !bookigns) return guestDetails;

  let booking = bookigns[0];

  switch (requiresDocuments) {
    case '1':
      return [guestDetails[0]];

    case 'num_adults':
      return guestDetails.slice(0, booking.adultsNumber);

    case 'num_guests':
      return guestDetails;

    case 'older_than_10':
    case 'older_over_12':
    case 'older_over_14':
    case 'older_over_16':
    case 'older_over_12':
      return guestDetails.slice(0, booking.adultsNumber);

    default:
      return guestDetails;
  }
}
